import React, { useContext, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

import InputSettingsField from './InputSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import CheckboxSettingsField from './CheckboxSettingsField';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import MetadataSettings from './MetadataSettings';
import Button from '../../misc/Button';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import RequiredLogicSettings from './RequiredLogicSettings';
import SettingsTabs from './SettingsTabs';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import { StyledFieldWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import FieldTypes from '../../../constants/FieldTypes';
import ThemeContext from '../../../theme/ThemeContext';
import ThemeIcons from '../../../theme/Icons';

import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';
import { validateReadableId } from '@builttocreate/engine-utils';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

/**
 * These field settings should be used for the following fields:
 * - block
 */
const FieldSettingsBlockField = ({

  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,
  requiredLogic,

  view,
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  fieldSection,

  type,
  x,
  y,
  width,
  height,
  value,
  metadata,
  config,

  //API Developers
  identifier,
  hidden,
  hiddenViews,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,
  margin,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onDuplicate,
  useReadableIds,

}) => {

  const theme = useContext(ThemeContext);
  const [readableIdError, setReadableIdError] = useState('');
  const prevProps = useRef();

  useEffect(() => {

    if (prevProps.current && prevProps.current !== fieldId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = fieldId;

  }, [fieldId, setReadableIdError]);


  const isRTEField = type === FieldTypes.richText;

  let nextFieldSettingsIcon = '';
  if (isRTEField) nextFieldSettingsIcon = ThemeIcons.fields.richText;
  else if (!value) nextFieldSettingsIcon = ThemeIcons.fields.blockEmptyspace;
  else if (fontSize >= 18) nextFieldSettingsIcon = ThemeIcons.fields.blockHeading;
  else if (fontSize <= 18) nextFieldSettingsIcon = ThemeIcons.fields.blockParagraph;

  const isStylesHidden = isSettingHidden(config, 'styles');
  const isValueHidden = isSettingHidden(config, 'value');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isVisibilityHidden = isSettingHidden(config, 'hiddenViews');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isIdHidden = isSettingHidden(config, '_id');

  //Text Styles
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const isHeaderOrFooter = fieldSection === 'header' || fieldSection === 'footer';

  const metadataOptions = getMetadataOptions(config);

  const stylesNode = (
    <>
      {(isRTEField || (
        isFontSizeHidden
        && isFontColorHidden
        && isTextAlignHidden
        && isFontStyleHidden
        && isFontWeightHidden
        && isTextTransformHidden
        && isTextDecorationHidden
      )) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Text"
            height={170}
          >
            <FieldStyleSettings
              id={`${fieldId}-field`}
              {...{

                fontSize,
                onFontSizeChange: isFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontSize: value }),

                fontColor,
                onFontColorChange: isFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontColor: value }),

                textAlign,
                onTextAlignChange: isTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textAlign: value }),

                fontStyle,
                onFontStyleChange: isFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontStyle: value }),

                fontWeight,
                onFontWeightChange: isFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontWeight: value }),

                textTransform,
                onTextTransformChange: isTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textTransform: value }),

                textDecoration,
                onTextDecorationChange: isTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textDecoration: value }),

              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isBorderColorHidden
        && isBorderRadiusHidden
        && isBorderWidthHidden
        && isBackgroundColorHidden
        && isMarginHidden
        && isPaddingHidden
        && isPositionHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={400}
        >
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{

              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

              backgroundColor,
              onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { backgroundColor: value }),

              padding,
              onPaddingChange: isRTEField || isPaddingHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

              margin,
              onMarginChange: isRTEField || isMarginHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { margin: value }),

              zIndex,
              onZIndexChange: isZIndexHidden ? null : (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                view,
                x,
                y,
                width,
                height,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
              }}
            />
          )}
        </AccordionSettings>
      )}
    </>
  );

  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={fieldId}
        type="text"
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onBlur={(value) => {

          if (value && !validateReadableId(value, fields) && value !== fieldId) {

            setReadableIdError(`Invalid: Can't be duplicate or start with numbers, or contain spaces.`);

          } else {

            setReadableIdError('');

            if (value && value !== fieldId) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { _id: value });
            }

          }
        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );


  const settingsNode = (
    <>
      {isValueHidden ? null : (
        <>
          <SettingsLabel>Field</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
        </>
      )}
      {isValueHidden || isRTEField ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="textarea"
            fieldId={fieldId}
            value={value}
            rows={3}
            onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { value })}
          />
        </StyledFieldWrapper>
      )}
      {isHeaderOrFooter || fields.length < 2 ? null : (
        <>
          <SettingsLabel>Required Logic</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledFieldWrapper>
            <RequiredLogicSettings
              fileId={fileId}
              targetLabel=" field"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={requiredLogic}
              onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { requiredLogic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {(isHiddenHidden && (isLogicHidden || fields.length < 2)) ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Conditional Logic</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            {isLogicHidden || fields.length < 2 ? null : (
              <StyledFieldWrapper>
                <ConditionalLogicSettings
                  fileId={fileId}
                  targetLabel=" field"
                  targetFieldId={fieldId}
                  sortedPages={sortedPages}
                  fields={fields}
                  logic={logic}
                  onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { logic: nextLogic })}
                />
              </StyledFieldWrapper>
            )}
            {isHiddenHidden ? null : (
              <StyledFieldWrapper>
                <CheckboxSettingsField
                  icon={ThemeIcons.settings.hide}
                  iconColor={hidden ? theme.warningColor : theme.borderColor}
                  id="hidden"
                  label="Hidden"
                  selected={hidden}
                  onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { hidden: selected },)}
                />
              </StyledFieldWrapper>
            )}
          </>
        )
      )}
      {isVisibilityHidden ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Visibility</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('desktop') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsDesktop"
                label="Hide on desktop"
                selected={hiddenViews?.includes('desktop') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'desktop']
                    : (hiddenViews || []).filter(v => v !== 'desktop');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('mobile') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsMobile"
                label="Hide on mobile"
                selected={hiddenViews?.includes('mobile') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'mobile']
                    : (hiddenViews || []).filter(v => v !== 'mobile');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('pdf') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsPdf"
                label="Hide on pdf"
                selected={hiddenViews?.includes('pdf') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'pdf']
                    : (hiddenViews || []).filter(v => v !== 'pdf');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
          </>
        )
      )}
      {!metadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
            />
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />

            <Button
              icon={ThemeIcons.settings.clone}
              text="Duplicate"
              onClick={() => onDuplicate(fieldId, fieldPositionId)}
              solid
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={nextFieldSettingsIcon}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );

};

export default FieldSettingsBlockField;
